/**
 * Putting the final checkout stage under the floating nav instead of behind it.
 *
 * The acknowledgments, the Free Flight Upgrade pick and the travel questions are
 * all INSERTED above the button the customer just pressed ("Continue to
 * Passenger Details"), and the passenger modal holds `body { overflow: hidden }`
 * for as long as it is open. Between the two, the page comes back scrolled to
 * roughly where that button used to be — which is now the middle of the new
 * block, with the terms checkbox off the top edge and the fixed nav pill
 * (~60px) over whatever is left of it. A checkbox nobody can see is a checkout
 * that stalls with no visible reason why, at the step where most attempts
 * already stall.
 *
 * The clearance for the nav comes from `scroll-margin-top` on the target
 * element (Tailwind `scroll-mt-28`), which `scrollIntoView` honours — not from
 * arithmetic here, which would have to know the header's height at every
 * breakpoint and would drift the first time the pill changes.
 */

/**
 * Scrolls the final-stage block to the top of the viewport. Called on the
 * transition INTO that stage only — see Review.jsx; on any other render the
 * caller must not call it, because re-aiming the viewport under someone who is
 * reading is worse than the clipping this fixes.
 *
 * Returns whether it scrolled, so a caller can tell a missing element from a
 * silent no-op.
 */
export function scrollFinalStageIntoView(element, { reduceMotion = false } = {}) {
  if (!element || typeof element.scrollIntoView !== 'function') return false;
  element.scrollIntoView({
    // Instant when the viewer asked for less motion: a long smooth scroll is
    // exactly the movement that setting exists to switch off.
    behavior: reduceMotion ? 'auto' : 'smooth',
    block: 'start',
    inline: 'nearest',
  });
  return true;
}

/**
 * True only when the viewer has actually asked for reduced motion. Anywhere
 * `matchMedia` is missing or throws (SSR, older jsdom, stubbed globals) this
 * returns false rather than guessing — an unwanted smooth scroll is a smaller
 * harm than an unexplained hard jump.
 */
export function prefersReducedMotion(win = typeof window === 'undefined' ? null : window) {
  try {
    return win?.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
  } catch {
    return false;
  }
}
